import type { JSX } from 'react';
import type { BrowserAgentSitesViewSite } from '@kirin/api-client/contracts/shadow-ai';
import { PlatformBadge } from '@/components/shared/platform';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { cn } from '@/lib/utils';
import {
  DECIDED_BY_LABEL,
  decidedBy,
  plural,
  stopRowClick,
  type ApprovalRecord,
} from './cellUtils';
import type { Enforcement } from './mockData';
import { formatZonedDate } from '@/lib/date-utils';

/** Cells for the Browser Agent Sites table (KIR-4704 product review). */

/** Agent badge, like the Inventory → Users clients (Claude agents get the Claude mark). */
export function AgentBadge({ name }: { name: string }): JSX.Element {
  return (
    <PlatformBadge
      platform={name.startsWith('Claude') ? 'claude' : name}
      label={name}
      showTooltip={false}
    />
  );
}

/** Via: two dot colors — direct fetch vs a browser extension (review #19). */
export function ViaCell({
  site,
}: {
  site: BrowserAgentSitesViewSite;
}): JSX.Element {
  const direct = site.agentType === 'claude_code_cli';
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span
        aria-hidden="true"
        className={cn(
          'h-2.5 w-2.5 shrink-0 rounded-full',
          direct
            ? 'bg-orange-400 dark:bg-orange-500/80'
            : 'bg-teal-500 dark:bg-teal-500/80'
        )}
      />
      <span className="truncate text-muted-foreground" title={site.reachedVia}>
        {site.reachedVia}
      </span>
    </span>
  );
}

const ENFORCEMENT_LABEL: Record<Enforcement, string> = {
  approve: 'Approve',
  block: 'Block',
  monitor: 'Monitor',
};

// Active segment = a raised surface pill on the muted track, with the option's
// accent as text/dot — reads as a pressed segmented-control button.
const ENFORCEMENT_ACTIVE: Record<Enforcement, string> = {
  approve:
    'bg-background text-green-700 shadow-sm dark:text-green-300 ring-1 ring-border',
  block:
    'bg-background text-red-700 shadow-sm dark:text-red-300 ring-1 ring-border',
  monitor: 'bg-background text-foreground shadow-sm ring-1 ring-border',
};

function EnforcementDot({ state }: { state: Enforcement }): JSX.Element {
  const color =
    state === 'approve'
      ? 'bg-green-500 dark:bg-green-400'
      : state === 'block'
        ? 'bg-red-500 dark:bg-red-400'
        : 'bg-muted-foreground';
  return <span className={cn('h-1.5 w-1.5 rounded-full', color)} />;
}

/** Approve / Block / Monitor segmented control; pointer + hover per review #5. */
export function EnforcementToggle({
  options,
  value,
  onChange,
  size = 'sm',
  disabled = false,
}: {
  options: Enforcement[];
  /** null = unreviewed → no segment highlighted (KIR-4703). */
  value: Enforcement | null;
  onChange: (next: Enforcement) => void;
  size?: 'sm' | 'lg';
  disabled?: boolean;
}): JSX.Element {
  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted p-0.5"
      onClick={stopRowClick}
    >
      {options.map(opt => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt)}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs',
              active
                ? ENFORCEMENT_ACTIVE[opt]
                : 'text-muted-foreground hover:bg-background hover:text-foreground hover:shadow-sm'
            )}
            aria-pressed={active}
          >
            <EnforcementDot state={opt} />
            {ENFORCEMENT_LABEL[opt]}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Approval column (review #18): no icon — the decision named in words, then
 * the date and one generic "Undo". Two 16px lines fit the 48px row.
 */
export function ApprovalCell({
  enforcement,
  approval,
  onUndo,
  disabled = false,
}: {
  enforcement: Enforcement | null;
  approval: ApprovalRecord | null;
  onUndo: () => void;
  disabled?: boolean;
}): JSX.Element {
  const timeZone = useDisplayTimeZone();
  if (!approval || !enforcement) {
    return <span className="text-sm text-muted-foreground">Not reviewed</span>;
  }
  const date = formatZonedDate(approval.date, timeZone, '');
  return (
    <div className="flex min-w-0 flex-col text-xs leading-4">
      <span
        className="truncate text-muted-foreground"
        title={decidedBy(enforcement, approval, timeZone)}
      >
        {DECIDED_BY_LABEL[enforcement]} {approval.by}
      </span>
      <span className="flex items-center gap-2">
        {date && <span className="text-muted-foreground">{date}</span>}
        <button
          type="button"
          onClick={event => {
            stopRowClick(event);
            onUndo();
          }}
          disabled={disabled}
          className="cursor-pointer font-medium text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-blue-400"
        >
          Undo
        </button>
      </span>
    </div>
  );
}

/** Users count; clickable (opens the users popup) when anyone is behind the row. */
export function UsersCount({
  site,
  onClick,
}: {
  site: BrowserAgentSitesViewSite;
  onClick: (site: BrowserAgentSitesViewSite) => void;
}): JSX.Element {
  if (site.users === 0) {
    return <span className="font-semibold">0</span>;
  }
  return (
    <button
      type="button"
      onClick={event => {
        stopRowClick(event);
        onClick(site);
      }}
      className="cursor-pointer font-semibold underline-offset-2 hover:underline"
      aria-label={`Show ${plural(site.users, 'user')} of ${site.siteHost}`}
    >
      {site.users}
    </button>
  );
}
